import type { SupabaseClient } from '@supabase/supabase-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface CandidateProfile {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  cv_storage_path: string | null;
  cv_filename: string | null;
  cv_uploaded_at: string | null;
  cv_parsed_at: string | null;
  cv_parsed_data: Record<string, unknown>;
  cv_completeness: number | null;
  created_at: string;
  updated_at: string;
}

export interface CandidateProfileUpdate {
  first_name?: string;
  last_name?: string;
  phone?: string;
  avatar_url?: string;
}

// ─── Profile hooks ────────────────────────────────────────────────────────────

export function useCandidateProfile(supabase: unknown, userId: string | undefined) {
  return useQuery({
    queryKey: ['candidate-profile', userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await (supabase as SupabaseClient)
        .from('hr_candidate_profiles')
        .select('*')
        .eq('user_id', userId as string)
        .maybeSingle();
      if (error) throw error;
      return data as CandidateProfile | null;
    },
  });
}

export function useUpdateCandidateProfile(supabase: unknown, userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CandidateProfileUpdate) => {
      const { error } = await (supabase as SupabaseClient).rpc('upsert_hr_candidate_profile', {
        p_first_name: input.first_name ?? null,
        p_last_name: input.last_name ?? null,
        p_phone: input.phone ?? null,
        p_avatar_url: input.avatar_url ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['candidate-profile', userId] }),
  });
}

// ─── CV hooks ─────────────────────────────────────────────────────────────────

export function useCvSignedUrl(supabase: unknown, storagePath: string | null | undefined) {
  return useQuery({
    queryKey: ['cv-signed-url', storagePath],
    enabled: Boolean(storagePath),
    staleTime: 1000 * 60 * 45,
    queryFn: async () => {
      const { data, error } = await (supabase as SupabaseClient).storage
        .from('private-cvs')
        .createSignedUrl(storagePath as string, 3600);
      if (error) throw error;
      return data.signedUrl;
    },
  });
}

export function useUploadCv(supabase: unknown, userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      if (!userId) throw new Error('unauthenticated');

      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${userId}/${Date.now()}_${safeName}`;

      const { error: uploadError } = await (supabase as SupabaseClient).storage
        .from('private-cvs')
        .upload(path, file, { upsert: true, contentType: file.type });
      if (uploadError) throw uploadError;

      const { error: dbError } = await (supabase as SupabaseClient)
        .from('hr_candidate_profiles')
        .upsert(
          {
            user_id: userId,
            cv_storage_path: path,
            cv_filename: file.name,
            cv_uploaded_at: new Date().toISOString(),
            cv_parsed_at: null,
            cv_parsed_data: {},
            cv_completeness: null,
          },
          { onConflict: 'user_id' },
        );
      if (dbError) throw dbError;

      // Fire-and-forget: trigger AI CV parse
      const supabaseClient = supabase as SupabaseClient;
      const {
        data: { session },
      } = await supabaseClient.auth.getSession();
      if (session?.access_token) {
        void supabaseClient.functions.invoke('ai-process-cv', {
          body: { cv_storage_path: path, user_id: userId },
        });
      }

      return path;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['candidate-profile', userId] }),
  });
}

export function useDeleteCv(supabase: unknown, userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (storagePath: string) => {
      const { error: deleteError } = await (supabase as SupabaseClient).storage
        .from('private-cvs')
        .remove([storagePath]);
      if (deleteError) throw deleteError;

      const { error: dbError } = await (supabase as SupabaseClient)
        .from('hr_candidate_profiles')
        .update({
          cv_storage_path: null,
          cv_filename: null,
          cv_uploaded_at: null,
          cv_parsed_data: {},
          cv_completeness: null,
        })
        .eq('user_id', userId as string);
      if (dbError) throw dbError;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['candidate-profile', userId] }),
  });
}
